/* Warehouse Bold mobile menu drawer.
 *
 * Owns three things the theme's drawer component does not do correctly on
 * iOS, plus the drill-down track:
 *
 *  1. A position:fixed BODY LOCK. The theme locks with overflow:hidden,
 *     which iOS ignores for touch — pans fall through and scroll the
 *     document behind the drawer (URL bar collapsing, page riding up).
 *  2. The bar-zone COVER. iOS Safari clips all fixed painting at the
 *     layout-viewport bottom; only ordinary document content paints in the
 *     floating-URL-bar zone, so a document-level absolute element conceals
 *     the page there. No height/overshoot/z-index on the drawer can.
 *  3. Order matters: pin the cover BEFORE locking — the fixed-body lock
 *     collapses the scroll range and clamps scrollY to 0, so the cover must
 *     read the real pre-lock scroll position.
 *
 * Every one of those was found on a real iPhone. Do not "simplify" them.
 */
(() => {
  const init = () => {
    const drawer = document.getElementById('MenuDrawer');
    const track = document.getElementById('wbMnavTrack');
    const rootView = document.getElementById('wbMnavRoot');
    const catView = document.getElementById('wbMnavCat');
    const grpView = document.getElementById('wbMnavGrp');
    const dataEl = document.getElementById('wbMenuData');
    if (!drawer || !track || !dataEl) return;

    /* PORTAL to body. The drawer ships inside <header>, whose stacking
       context traps it: the header's own bars paint over the drawer's head
       (elementFromPoint on the ✕ returned .header__top) no matter how high
       its z-index goes, because z-index only ranks within a context. The
       theme's own DrawerComponent does the same thing for the same reason
       (requiresBodyAppended). Body is the only safe parent. */
    if (drawer.parentElement !== document.body) document.body.appendChild(drawer);

    let DATA;
    try { DATA = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const S = DATA.strings || {};
    const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

    /* ---------- iOS: bar-zone cover + real scroll lock ---------- */
    const cover = document.getElementById('wbMnavCover');
    let lockedY = 0;
    const showCover = () => {
      if (!cover || window.innerWidth >= 750) return;
      cover.style.top = (window.scrollY || document.documentElement.scrollTop || 0) + 'px';
      cover.hidden = false;
    };
    const hideCover = () => { if (cover) cover.hidden = true; };
    const lock = () => {
      lockedY = window.scrollY || document.documentElement.scrollTop || 0;
      const b = document.body.style;
      b.position = 'fixed';
      b.top = -lockedY + 'px';
      b.left = '0';
      b.width = '100%';
    };
    const unlock = () => {
      const b = document.body.style;
      b.position = ''; b.top = ''; b.left = ''; b.width = '';
      /* Restore INSTANTLY — html carries scroll-behavior:smooth and a
         glide back to the pre-lock position reads as the page scrolling
         itself. */
      const prev = document.documentElement.style.scrollBehavior;
      document.documentElement.style.scrollBehavior = 'auto';
      window.scrollTo(0, lockedY);
      document.documentElement.style.scrollBehavior = prev;
    };

    /* ---------- drill-down track ---------- */
    /* RESTING state is margin-left (layout); the transform exists only for
       the ~300ms glide, then settle() clears it. A persistent ancestor
       transform drops the panel scrollers off iOS's fast compositor path
       and kills flick momentum mid-list. */
    let shown = 0, pending = null, slideT = null;
    const settle = (level) => {
      track.style.transition = 'none';
      track.style.transform = '';
      track.style.marginLeft = (-level * 100) + '%';
      void track.offsetWidth; // commit the settled position first
      track.style.transition = '';
      shown = level;
      pending = null;
    };
    track.addEventListener('transitionend', (e) => {
      if (e.target === track && e.propertyName === 'transform' && pending !== null) settle(pending);
    });
    const go = (level, anim) => {
      clearTimeout(slideT);
      if (anim === false || reduced()) { settle(level); return; }
      pending = level;
      // % of the TRACK (300% wide): one panel = 33.3333%.
      track.style.transform = 'translateX(' + ((shown - level) * 33.3333) + '%)';
      slideT = setTimeout(() => { if (pending !== null) settle(pending); }, 380);
    };

    /* ---------- panel builders (same data as the desktop panel) ---------- */
    const tile = (t) => '<a class="ftile" href="' + esc(t.url) + '"><span class="img">' +
      (t.img ? '<img src="' + esc(t.img) + '" alt="" loading="lazy">' : '<span class="ph">' + esc(t.title) + '</span>') +
      '</span><span class="lbl">' + esc(t.title) + '</span></a>';

    const catHtml = (i) => {
      const c = DATA.categories[i];
      let h = '<button type="button" class="mn-back" data-mn-back="0"><span class="b" aria-hidden="true">&lsaquo;</span> ' + esc(S.mainMenu || 'Main menu') + '</button>' +
        '<div class="mn-ttl">' + esc(c.title) + '</div>' +
        '<a class="mn-row shopall" href="' + esc(c.url) + '">' +
        esc((S.shopAllCategory || 'Shop All __CAT__').replace('__CAT__', c.title)) +
        '<span class="arr" aria-hidden="true">&rarr;</span></a>';
      if (c.featured && c.featured.length) {
        h += '<div class="mn-feat"><div class="cap">' + esc(S.popular || 'Popular') + '</div>' +
          '<div class="mn-featrow">' + c.featured.map(tile).join('') + '</div></div>';
      }
      h += c.groups.map((g, gi) => g.children.length
        ? '<button type="button" class="mn-row" data-mn-grp="' + gi + '">' + esc(g.title) + '<span class="chev" aria-hidden="true">&rsaquo;</span></button>'
        : '<a class="mn-row" href="' + esc(g.url) + '">' + esc(g.title) + '</a>' // zero-child: navigates
      ).join('');
      return h;
    };

    const grpHtml = (ci, gi) => {
      const c = DATA.categories[ci], g = c.groups[gi];
      return '<button type="button" class="mn-back" data-mn-back="1"><span class="b" aria-hidden="true">&lsaquo;</span> ' + esc(c.title) + '</button>' +
        '<div class="mn-ttl">' + esc(g.title) + '</div>' +
        '<a class="mn-row shopall" href="' + esc(g.url) + '">' +
        esc((S.allGroup || 'All __GRP__').replace('__GRP__', g.title)) +
        '<span class="arr" aria-hidden="true">&rarr;</span></a>' +
        g.children.map((ch) => '<a class="mn-row plain" href="' + esc(ch.url) + '">' + esc(ch.title) + '</a>').join('');
    };

    /* ---------- brands view ----------
       Rendered into the CATEGORY view (level 1), because "all brands" sits at
       the same depth as "a category" — a sibling of Avionics, not a child of
       anything. That keeps the 3-panel track and its 33.3333% maths exactly
       as they are; a fourth panel would have meant re-deriving every slide.

       Reuses the desktop panel's own classes (.wb-brands__*), which already
       carry a 3-column grid and height-capped logos below 750px. One set of
       tile styles, two menus — the same rule this drawer follows for the
       category data. */
    const brandsHtml = () => {
      /* ⚠ Digit-led names sort AFTER Z, not before A — "#" belongs at the end
         of an alphabet. The group flag does that; the name breaks ties. */
      const AZ = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      const grp = (n) => (AZ.indexOf(n.charAt(0).toUpperCase()) >= 0 ? 0 : 1);
      const list = (DATA.brands || []).slice().sort((a, b) =>
        grp(a.name) - grp(b.name) ||
        a.name.toLowerCase().localeCompare(b.name.toLowerCase(), 'en'));
      if (!list.length) return '';

      /* ⚠ No A–Z rail and no letter headings here, unlike desktop. 84 brands
         over 22 letters is under four per letter, so at three columns almost
         every group was one ragged row under its own heading — the headings
         cost more in chop than they returned in orientation, and the rail was
         navigation for a distance nobody scrolls (Owen, 2026-08-14). One
         continuous alphabetical grid instead; desktop keeps both, where six
         columns and a fixed-height panel make them earn their place. */
      return '<button type="button" class="mn-back" data-mn-back="0">' +
        '<span class="b" aria-hidden="true">&lsaquo;</span> ' + esc(S.mainMenu || 'Main menu') + '</button>' +
        '<div class="mn-ttl">' + esc(S.shopByBrand || 'Shop by Brand') + '</div>' +
        '<ul class="wb-brands__grid mn-brandgrid" role="list">' +
        list.map((b) =>
          '<li><a class="wb-brands__tile" href="' + esc(b.url) + '"><span class="wb-brands__mark">' +
          (b.img
            ? '<img src="' + esc(b.img) + '" alt="" loading="lazy" decoding="async">'
            : '<span class="wb-brands__wordmark">' + esc(b.name) + '</span>') +
          '</span><span class="wb-brands__name">' + esc(b.name) + '</span></a></li>'
        ).join('') +
        '</ul>';
    };

    let catIdx = 0;
    drawer.addEventListener('click', (e) => {
      const b = e.target.closest('[data-mn-cat],[data-mn-grp],[data-mn-back],[data-mn-brands]');
      if (!b) return;
      if (b.hasAttribute('data-mn-brands')) {
        catView.innerHTML = brandsHtml();
        catView.scrollTop = 0;
        go(1);
      } else if (b.hasAttribute('data-mn-cat')) {
        catIdx = +b.getAttribute('data-mn-cat');
        catView.innerHTML = catHtml(catIdx);
        catView.scrollTop = 0;
        go(1);
      } else if (b.hasAttribute('data-mn-grp')) {
        grpView.innerHTML = grpHtml(catIdx, +b.getAttribute('data-mn-grp'));
        grpView.scrollTop = 0;
        go(2);
      } else {
        go(+b.getAttribute('data-mn-back'));
      }
    });

    /* ---------- open / close ---------- */
    let closing = false;
    const open = () => {
      if (!drawer.hidden) return;
      go(0, false);              // every open starts at the root, instantly
      rootView.scrollTop = 0;
      drawer.hidden = false;
      showCover();               // BEFORE lock() — see the header note
      lock();
      /* rAF + timeout both arm the slide (idempotent): rAF alone never
         fires in a hidden tab, leaving the drawer open but off-screen. */
      requestAnimationFrame(() => drawer.classList.add('is-open'));
      setTimeout(() => drawer.classList.add('is-open'), 40);
      drawer.setAttribute('aria-hidden', 'false');
      const x = drawer.querySelector('.mnav__x');
      if (x) x.focus({ preventScroll: true });
    };
    const close = () => {
      if (drawer.hidden || closing) return;
      closing = true;
      hideCover();               // close-START: the exit slide plays over the page
      drawer.classList.remove('is-open');
      setTimeout(() => {
        drawer.hidden = true;
        drawer.setAttribute('aria-hidden', 'true');
        unlock();
        closing = false;
      }, 300);
      const trigger = document.querySelector('.menu-drawer-button');
      if (trigger) trigger.focus({ preventScroll: true });
    };

    document.querySelectorAll('[aria-controls="MenuDrawer"], .menu-drawer-button').forEach((btn) => {
      btn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); open(); }, true);
    });
    drawer.querySelectorAll('[data-close-drawer]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !drawer.hidden) close(); });
    /* Crossing to desktop while open: the mega panel owns ≥750px. */
    addEventListener('resize', () => { if (window.innerWidth >= 750 && !drawer.hidden) close(); }, { passive: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
